import {
  useRecapDecisionById,
  useRecapModifyDecision,
} from "@/features/hooks/useRecap";
import { DecisionItem } from "@/interfaces/IRecap";
import { Icon, ResponsiveModal } from "@nexus/shared-ui";
import { Button, Flex, Form } from "antd";
import TextArea from "antd/es/input/TextArea";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

const ModifyDecision = ({
  open,
  data,
  sessionId,
  onClose,
}: {
  open: boolean;
  data?: DecisionItem | null;
  sessionId: string;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const { data: decisionDetail, isFetching: isFetchingDecision } =
    useRecapDecisionById(data?.id);

  const { mutate: modifyDecision, isPending } = useRecapModifyDecision();

  useEffect(() => {
    if (!open) return;

    if (data?.id) {
      form.setFieldsValue({
        title: decisionDetail?.title || data?.title || "",
      });
    } else {
      form.resetFields();
    }
  }, [open, data?.id, decisionDetail?.title, data?.title, form]);

  const handleClose = useCallback(() => {
    form.resetFields();
    onClose();
  }, [form, onClose]);

  const onSubmitForm = useCallback(
    (values: { title: string }) => {
      modifyDecision(
        {
          sessionId,
          id: data?.id || "",
          title: values.title,
        },
        {
          onSuccess: () => {
            handleClose();
          },
        }
      );
    },
    [modifyDecision, sessionId, data?.id, handleClose]
  );

  const title = data?.id ? t("recap.editDecision") : t("recap.addDecision");

  const formContent = useMemo(
    () => (
      <Form form={form} layout="vertical" onFinish={onSubmitForm}>
        <Form.Item
          name="title"
          rules={[
            {
              required: true,
              whitespace: true,
              message: t("recap.titleRequired"),
            },
          ]}
        >
          <TextArea rows={4} placeholder={t("recap.decisionTitle")} />
        </Form.Item>
      </Form>
    ),
    [form, onSubmitForm, t, isFetchingDecision]
  );

  return (
    <ResponsiveModal
      open={open}
      title={title}
      onClose={handleClose}
      destroyOnHidden
      drawerProps={{
        placement: "right",
        width: "100%",
      }}
      drawerFooter={
        <Flex gap={8}>
          <Button
            className="w-100"
            type="primary"
            loading={isPending}
            icon={<Icon name="UilSave" size={16} color="white" />}
            onClick={() => form.submit()}
          >
            {t("actions.save")}
          </Button>

          <Button className="w-100" onClick={handleClose}>
            {t("actions.cancel")}
          </Button>
        </Flex>
      }
      modalProps={{
        footer: (
          <Flex justify="flex-end" gap={8}>
            <Button onClick={handleClose}>{t("actions.cancel")}</Button>
            <Button
              type="primary"
              loading={isPending}
              onClick={() => form.submit()}
            >
              {t("actions.save")}
            </Button>
          </Flex>
        ),
      }}
    >
      {formContent}
    </ResponsiveModal>
  );
};

export default ModifyDecision;
